import { useState } from "react";
import { useAdminAuth } from "./useAdminAuth.js";
import { AdminLogin } from "./AdminLogin.js";
import { PortfolioAdmin } from "./PortfolioAdmin.js";
import { PricingAdmin } from "./PricingAdmin.js";
import { FaqAdmin } from "./FaqAdmin.js";
import { CalendarAdmin } from "./CalendarAdmin.js";
import { BookingsAdmin } from "./BookingsAdmin.js";
import { TroubleshootingAdmin } from "./TroubleshootingAdmin.js";
import { InsightsAdmin } from "./InsightsAdmin.js";

/** The selectable management views. */
type AdminTab =
  | "bookings"
  | "portfolio"
  | "pricing"
  | "faq"
  | "calendars"
  | "insights"
  | "troubleshooting";

const TABS: readonly { id: AdminTab; label: string }[] = [
  { id: "bookings", label: "Bookings" },
  { id: "portfolio", label: "Portfolio" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
  { id: "calendars", label: "Calendars" },
  { id: "insights", label: "Insights" },
  { id: "troubleshooting", label: "Troubleshooting" },
];

/**
 * Owner admin application (task 18.7).
 *
 * Gated behind an owner session: until a token is supplied the login form is
 * shown; once signed in, a tabbed shell exposes the management views
 * (portfolio, hero image, pricing, FAQ, payment labels, calendar connections),
 * each of which calls its management route with the bearer token. Mounted at
 * the `#admin` route by `main.tsx`, separate from the public site.
 */
export function AdminApp(): JSX.Element {
  const auth = useAdminAuth();
  const [tab, setTab] = useState<AdminTab>("bookings");
  const [menuOpen, setMenuOpen] = useState(false);

  // Avoid flashing the login form while a persisted session is being restored.
  if (auth.status === "loading") {
    return (
      <div className="admin-login">
        <p className="admin-login__hint" role="status">
          {"Loading\u2026"}
        </p>
      </div>
    );
  }

  if (!auth.isAuthenticated || auth.token === null) {
    return <AdminLogin onSignedIn={auth.setToken} />;
  }

  const token = auth.token;

  return (
    <div className="admin">
      <header className="admin__bar">
        <span className="admin__brand">Studio Admin</span>

        {/* Mobile-only toggle: shows the current section + a hamburger/close
            icon and opens the dropdown menu below. Hidden on desktop. */}
        <button
          type="button"
          className="admin__menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="admin-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="admin__menu-current">
            {TABS.find((entry) => entry.id === tab)?.label ?? "Menu"}
          </span>
          <span className="admin__menu-icon" aria-hidden="true">
            {menuOpen ? "\u2715" : "\u2630"}
          </span>
        </button>

        <nav
          id="admin-nav"
          className={menuOpen ? "admin__tabs admin__tabs--open" : "admin__tabs"}
          aria-label="Admin sections"
        >
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={
                tab === entry.id
                  ? "admin__tab admin__tab--active"
                  : "admin__tab"
              }
              aria-current={tab === entry.id ? "page" : undefined}
              onClick={() => {
                setTab(entry.id);
                setMenuOpen(false);
              }}
            >
              {entry.label}
            </button>
          ))}
          {/* Sign out lives at the bottom of the dropdown on mobile. */}
          <button
            type="button"
            className="admin-button admin__signout--menu"
            onClick={auth.signOut}
          >
            Sign out
          </button>
        </nav>

        {/* Desktop sign out (hidden on mobile, where it moves into the menu). */}
        <button
          type="button"
          className="admin-button admin__signout--bar"
          onClick={auth.signOut}
        >
          Sign out
        </button>
      </header>

      <main className="admin__content">
        {tab === "bookings" ? <BookingsAdmin token={token} /> : null}
        {tab === "portfolio" ? <PortfolioAdmin token={token} /> : null}
        {tab === "pricing" ? <PricingAdmin token={token} /> : null}
        {tab === "faq" ? <FaqAdmin token={token} /> : null}
        {tab === "calendars" ? <CalendarAdmin token={token} /> : null}
        {tab === "insights" ? <InsightsAdmin token={token} /> : null}
        {tab === "troubleshooting" ? (
          <TroubleshootingAdmin token={token} />
        ) : null}
      </main>
    </div>
  );
}
